import React, { useEffect, useState } from 'react';
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, MenuItem, TextField,
} from '@mui/material';
import { LeadService } from '../../services/lead.service';
import {
  Lead, LeadCreateDTO, LeadSource, LEAD_SOURCE_LABELS,
} from '../../interfaces/crm/crm.interface';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';

interface LeadFormProps {
  open: boolean;
  lead?: Lead | null;
  onClose: () => void;
  onSaved: () => void;
}

const LEAD_SOURCES: LeadSource[] = ['WEBSITE', 'REFERRAL', 'COLD_CALL', 'EVENT', 'OTHER'];

const LeadForm: React.FC<LeadFormProps> = ({ open, lead, onClose, onSaved }) => {
  const toast = useToast();
  const isEdit = Boolean(lead);

  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [source, setSource] = useState<LeadSource>('OTHER');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Sync form fields whenever the dialog opens (new vs edit).
  useEffect(() => {
    if (!open) return;
    setName(lead?.name ?? '');
    setCompanyName(lead?.companyName ?? '');
    setEmail(lead?.email ?? '');
    setPhone(lead?.phone ?? '');
    setSource(lead?.source ?? 'OTHER');
    setNotes(lead?.notes ?? '');
  }, [open, lead]);

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (name.trim() === '') return;
    setSaving(true);
    try {
      const institutionId = AuthService.getSelectedInstitution();
      const dto: LeadCreateDTO = {
        name: name.trim(),
        companyName: companyName.trim() || undefined,
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        source,
        notes: notes.trim() || undefined,
        institutionId,
      };
      if (isEdit && lead) {
        await LeadService.update(lead.id, dto);
        toast.showSuccess('Lead updated');
      } else {
        await LeadService.create(dto);
        toast.showSuccess('Lead created');
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.showApiError(e, isEdit ? 'Failed to update lead' : 'Failed to create lead');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" data-help-page="crm.leadForm.page">
      <DialogTitle>{isEdit ? 'Edit Lead' : 'New Lead'}</DialogTitle>
      <DialogContent>
        <Box data-help-section="crm.leadForm.section.details" sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            data-help="crm.leadForm.field.name"
            label="Name"
            value={name}
            onChange={e => setName(e.target.value)}
            required
          />
          <TextField
            data-help="crm.leadForm.field.company"
            label="Company Name"
            value={companyName}
            onChange={e => setCompanyName(e.target.value)}
          />
          <TextField
            data-help="crm.leadForm.field.email"
            label="Email"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
          />
          <TextField
            data-help="crm.leadForm.field.phone"
            label="Phone"
            value={phone}
            onChange={e => setPhone(e.target.value)}
          />
          <TextField
            data-help="crm.leadForm.field.source"
            select
            label="Source"
            value={source}
            onChange={e => setSource(e.target.value as LeadSource)}
          >
            {LEAD_SOURCES.map(s => (
              <MenuItem key={s} value={s}>{LEAD_SOURCE_LABELS[s]}</MenuItem>
            ))}
          </TextField>
          <TextField
            data-help="crm.leadForm.field.notes"
            label="Notes"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            multiline
            minRows={3}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button data-help="crm.leadForm.cancel" onClick={handleClose} disabled={saving}>Cancel</Button>
        <Button
          data-help="crm.leadForm.save"
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || name.trim() === ''}
        >
          {saving ? <CircularProgress size={20} /> : isEdit ? 'Save Changes' : 'Create Lead'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LeadForm;
